import { Component, OnInit, computed, inject, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Observable, forkJoin, of } from 'rxjs';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { QuillEditorComponent } from '@shared/components/quill-editor/quill-editor.component';
import {
  FileAttachment,
  FileAttachmentListComponent,
  ImageEditedEvent
} from '@shared/components/file-attachment-list/file-attachment-list.component';
import { DiagramListComponent, DiagramSaveEvent } from '../diagram-list/diagram-list.component';
import { GdpSuggestionService } from '../../services/gdp-suggestion.service';
import { GdpDiagramService } from '../../services/gdp-diagram.service';
import { GdpNotifyService } from '../../services/gdp-notify.service';
import { Diagram, DiagramContent, DiagramRequest } from '../../models/diagram.model';
import { QaTask, Suggestion, SuggestionStatus, SuggestionType } from '../../models/suggestion.model';

/** Archivo aún no subido, con id local estable para las miniaturas. */
interface PendingFile {
  id: number;
  file: File;
}

/** Diagrama draw.io aún no subido, con id local estable para las miniaturas. */
interface PendingDiagram {
  id: number;
  request: DiagramRequest;
}

/**
 * Formulario reutilizable para crear/editar sugerencias, con adjuntos
 * pendientes (se suben tras guardar). Se usa en la página de sugerencias
 * y en el diálogo rápido del sidebar. Los adjuntos pendientes se pintan
 * con los mismos componentes de miniatura + edición que el detalle de
 * tarea (imágenes editables con el pincel, diagramas reabribles).
 */
@Component({
  selector: 'app-suggestion-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    TranslatePipe,
    QuillEditorComponent,
    FileAttachmentListComponent,
    DiagramListComponent
  ],
  templateUrl: './suggestion-form.component.html',
  styleUrls: ['./suggestion-form.component.css']
})
export class SuggestionFormComponent implements OnInit {
  private readonly suggestionService = inject(GdpSuggestionService);
  private readonly diagramService = inject(GdpDiagramService);
  private readonly notify = inject(GdpNotifyService);
  private readonly fb = inject(FormBuilder);

  /** Emite la sugerencia creada/actualizada (tras subir los adjuntos pendientes). */
  readonly saved = output<Suggestion>();

  /** Tareas de sprints abiertos donde soy QA: habilitan el tipo QA Feedback. */
  readonly qaTasks = signal<QaTask[]>([]);
  readonly suggestionTypes = computed<SuggestionType[]>(() =>
    this.qaTasks().length > 0
      ? ['ERROR', 'NEW_IDEA', 'IMPROVEMENT', 'QA_FEEDBACK']
      : ['ERROR', 'NEW_IDEA', 'IMPROVEMENT']
  );
  /** Archivos seleccionados antes de enviar (se suben tras crear la sugerencia) */
  readonly pendingFiles = signal<PendingFile[]>([]);
  /** Diagramas draw.io creados antes de enviar (se suben tras crear la sugerencia) */
  readonly pendingDiagrams = signal<PendingDiagram[]>([]);
  /** Si se está editando una sugerencia existente, su id */
  readonly editingId = signal<number | null>(null);
  /** Estado de la sugerencia en edición (null si es nueva). */
  readonly editingStatus = signal<SuggestionStatus | null>(null);

  /** "Guardar como borrador" solo tiene sentido creando o retocando un borrador. */
  readonly canSaveAsDraft = computed(() =>
    this.editingId() === null || this.editingStatus() === 'DRAFT'
  );

  /** Editando algo ya enviado solo se guardan cambios (el estado no se toca). */
  readonly isEditingSent = computed(() =>
    this.editingId() !== null && this.editingStatus() !== 'DRAFT'
  );

  /** Ids locales de adjuntos pendientes (nuevo id = miniatura recargada). */
  private nextPendingId = 1;

  /** Los pendientes vistos como adjuntos para la lista compartida de miniaturas. */
  readonly pendingAttachments = computed<FileAttachment[]>(() =>
    this.pendingFiles().map(pending => ({
      id: pending.id,
      fileName: pending.file.name,
      contentType: pending.file.type || null,
      fileSize: pending.file.size
    }))
  );

  /** Los diagramas pendientes vistos como diagramas para la lista compartida. */
  readonly pendingDiagramItems = computed<Diagram[]>(() =>
    this.pendingDiagrams().map(pending => ({
      id: pending.id,
      name: pending.request.name,
      createdAt: '',
      updatedAt: null,
      hasPreview: !!pending.request.previewPngBase64
    }))
  );

  /** Miniaturas de los archivos pendientes: el binario ya está en memoria. */
  readonly pendingFileLoader = (attachment: FileAttachment): Observable<Blob> => {
    const pending = this.pendingFiles().find(p => p.id === attachment.id);
    return of(pending ? (pending.file as Blob) : new Blob());
  };

  /** Miniaturas de los diagramas pendientes: la preview PNG viaja en base64. */
  readonly pendingDiagramPreviewLoader = (diagram: Diagram): Observable<Blob> => {
    const pending = this.pendingDiagrams().find(p => p.id === diagram.id);
    return of(this.base64ToBlob(pending?.request.previewPngBase64 ?? null));
  };

  /** XML de los diagramas pendientes para reabrir el editor draw.io. */
  readonly pendingDiagramContentLoader = (diagram: Diagram): Observable<DiagramContent> => {
    const pending = this.pendingDiagrams().find(p => p.id === diagram.id);
    return of({ id: diagram.id, name: pending?.request.name ?? diagram.name, xml: pending?.request.xml ?? '' });
  };

  form = this.fb.group({
    type: this.fb.control<SuggestionType>('IMPROVEMENT', { nonNullable: true, validators: Validators.required }),
    title: ['', [Validators.required, Validators.maxLength(255)]],
    text: [null as string | null],
    taskId: [null as number | null]
  });

  constructor() {
    // taskId es obligatorio solo para QA Feedback
    this.form.controls.type.valueChanges.subscribe(type => {
      const taskControl = this.form.controls.taskId;
      if (type === 'QA_FEEDBACK') {
        taskControl.addValidators(Validators.required);
      } else {
        taskControl.removeValidators(Validators.required);
        taskControl.setValue(null);
      }
      taskControl.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.suggestionService.getMyQaTasks().subscribe(tasks => this.qaTasks.set(tasks));
  }

  // ── Archivos pendientes ─────────────────────────────────────────────────────

  addPendingFiles(files: File[]): void {
    this.pendingFiles.update(current => [
      ...current,
      ...files.map(file => ({ id: this.nextPendingId++, file }))
    ]);
  }

  removePendingFile(attachment: FileAttachment): void {
    this.pendingFiles.update(current => current.filter(p => p.id !== attachment.id));
  }

  downloadPendingFile(attachment: FileAttachment): void {
    const pending = this.pendingFiles().find(p => p.id === attachment.id);
    if (!pending) {
      return;
    }
    const url = window.URL.createObjectURL(pending.file);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = pending.file.name;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  /** Imagen pendiente editada en el paint: se sustituye en memoria (id nuevo = miniatura fresca). */
  onPendingImageEdited(event: ImageEditedEvent): void {
    const baseName = event.attachment.fileName.replace(/\.[^.]+$/, '');
    const edited = new File([event.blob], `${baseName}.png`, { type: 'image/png' });
    this.pendingFiles.update(current => current.map(pending =>
      pending.id === event.attachment.id
        ? { id: this.nextPendingId++, file: edited }
        : pending
    ));
  }

  // ── Diagramas pendientes ────────────────────────────────────────────────────

  /** Alta (diagram null) o edición de un diagrama pendiente desde la lista compartida. */
  onPendingDiagramSave(event: DiagramSaveEvent): void {
    if (event.diagram === null) {
      this.pendingDiagrams.update(current => [
        ...current,
        { id: this.nextPendingId++, request: event.request }
      ]);
      return;
    }
    const editedId = event.diagram.id;
    this.pendingDiagrams.update(current => current.map(pending =>
      pending.id === editedId
        ? { id: this.nextPendingId++, request: event.request }
        : pending
    ));
  }

  removePendingDiagram(diagram: Diagram): void {
    this.pendingDiagrams.update(current => current.filter(p => p.id !== diagram.id));
  }

  private base64ToBlob(base64: string | null): Blob {
    if (!base64) {
      return new Blob([], { type: 'image/png' });
    }
    const raw = base64.includes(',') ? base64.slice(base64.indexOf(',') + 1) : base64;
    try {
      const decoded = atob(raw);
      const bytes = new Uint8Array(decoded.length);
      for (let i = 0; i < decoded.length; i++) {
        bytes[i] = decoded.charCodeAt(i);
      }
      return new Blob([bytes], { type: 'image/png' });
    } catch {
      return new Blob([], { type: 'image/png' });
    }
  }

  // ── Envío ───────────────────────────────────────────────────────────────────

  /**
   * Guarda la sugerencia. draft=true → borrador (sin notificar);
   * draft=false → enviar (un borrador pasa a SENT y recién entonces
   * notifica); draft=undefined → conservar el estado actual (edición
   * de una sugerencia ya enviada).
   */
  submit(draft?: boolean): void {
    if (this.form.invalid) {
      return;
    }
    const request = {
      type: this.form.getRawValue().type,
      title: this.form.value.title!,
      text: this.form.value.text ?? null,
      taskId: this.form.getRawValue().type === 'QA_FEEDBACK' ? this.form.value.taskId : null,
      draft
    };
    const editingId = this.editingId();
    const save$ = editingId !== null
      ? this.suggestionService.updateSuggestion(editingId, request)
      : this.suggestionService.createSuggestion(request);

    save$.subscribe({
      next: suggestion => {
        // Sube los archivos y diagramas pendientes tras crear/actualizar
        const uploads: Observable<unknown>[] = [
          ...this.pendingFiles().map(pending => this.suggestionService.uploadFile(suggestion.id, pending.file)),
          ...this.pendingDiagrams().map(pending =>
            this.diagramService.createSuggestionDiagram(suggestion.id, pending.request))
        ];
        this.notify.success(
          draft === true
            ? 'gdp.suggestions.draft-saved'
            : editingId !== null && this.isEditingSent() ? 'gdp.suggestions.updated' : 'gdp.suggestions.sent'
        );
        this.resetForm();
        if (uploads.length === 0) {
          this.saved.emit(suggestion);
          return;
        }
        // Avisa al contenedor cuando los adjuntos ya están subidos
        forkJoin(uploads).subscribe({
          next: () => this.saved.emit(suggestion),
          error: err => {
            this.notify.error(err);
            this.saved.emit(suggestion);
          }
        });
      },
      error: err => this.notify.error(err)
    });
  }

  /** Carga una sugerencia existente en el formulario (modo edición). */
  edit(suggestion: Suggestion): void {
    this.editingId.set(suggestion.id);
    this.editingStatus.set(suggestion.status);
    this.form.setValue({
      type: suggestion.type,
      title: suggestion.title,
      text: suggestion.text,
      taskId: suggestion.taskId
    });
  }

  resetForm(): void {
    this.editingId.set(null);
    this.editingStatus.set(null);
    this.pendingFiles.set([]);
    this.pendingDiagrams.set([]);
    this.form.reset({ type: 'IMPROVEMENT', title: '', text: null, taskId: null });
  }
}
